import { useMutation } from '@tanstack/react-query';
import { Col, Form, Input, Modal, ModalProps, Row, Space } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { SalesInfoApis } from 'apis/SalesInfoApis';
import { XIcon } from 'assets/svg';
import { Button, ButtonAction, InputForm, SelectForm } from 'components';
import {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import TableComponent from 'components/Table/TableComponent';
import {
  IDataGrouped,
  IGetAllConditionTypeParams,
  IOrder,
  Result,
} from 'interfaces/SalesInfo';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import formatNumber, {
  formatCurrency,
  toastError,
} from 'utils/constants/functions';

type ManualPromotionModalProps = Omit<ModalProps, 'onOk' | 'onCancel'> & {
  data: Result[];
  productsApplied: Result[];
  orderData: IOrder;
  selectedRecord: IDataGrouped | undefined;
  onOk?(records: Partial<Result>[]): void;
  onCancel: () => void;
  onBeforeOk?: (record: Partial<Result>[]) => { isExist: boolean };
};

const colSpan = 6;
const MANUAL_VOUCHER = {
  Voucher: null,
  VoucherName: null,
  Serial: null,
  Status: null,
  SerialFromDate: null,
  SerialToDate: null,
  LimitAmount: null,
  MinimumBilling: null,
  SaleDealFromDate: null,
  SaleDealToDate: null,
};

export const ManualPromotionModal = memo(
  ({
    data,
    orderData,
    selectedRecord,
    productsApplied,
    open,
    onCancel,
    onOk,
    onBeforeOk,
  }: ManualPromotionModalProps) => {
    const [form] = Form.useForm();
    const [tableForm] = Form.useForm();

    const productStored = useRef<Partial<Result>[]>();

    const [conditionTypes, setConditionTypes] =
        useState<ILazySelectData>(initLazySelectData),
      [saleDeals, setSaleDeals] = useState([] as DefaultOptionType[]),
      [products, setProducts] = useState([] as Partial<Result>[]),
      [conditionValue, setConditionValue] = useState<number | undefined>(
        undefined,
      ),
      [currentProducts, setCurrentProducts] = useState([] as Result[]);

    const {
        mutate: getAllConditionTypesMutate,
        isPending: isGetAllConditionTypesPending,
      } = useMutation({
        mutationFn: (params: IGetAllConditionTypeParams) =>
          SalesInfoApis.getAllConditionTypes(params),
        onSuccess: (res: any) => {
          if (res?.data?.getConditionType) {
            const items = res?.data?.getConditionType?.items ?? [];

            setConditionTypes({
              totalItems:
                res?.data?.getConditionType?.pageInfo?.totalItems ?? 0,
              options: items.map(
                (item: any) =>
                  ({
                    label: `${item?.id} - ${item?.name}`,
                    value: item?.id,
                    title: item?.value,
                  }) as DefaultOptionType,
              ),
            });

            return;
          }

          if (res?.errors)
            toastError(
              `Lỗi khi lấy danh sách Condition Type. ${res?.errors[0]?.message}`,
            );
        },
        onError: (error) => {
          toastError(`Lỗi khi lấy danh sách Condition Type. ${error?.message}`);
        },
      }),
      { mutate: getSaleDealsMutate, isPending: isGetSaleDealsPending } =
        useMutation({
          mutationFn: (orderId: string) => SalesInfoApis.getSaleDeals(orderId),
          onSuccess: (res: any) => {
            if (res?.data?.Output?.results)
              setSaleDeals(
                (res?.data?.Output?.results ?? []).map(
                  (item: any) =>
                    ({
                      label: item?.TenCT || item?.SaleDetalName,
                      value: `${item?.MaCT}_${item?.Serial}`,
                    }) as DefaultOptionType,
                ),
              );
          },
        });

    useEffect(() => {
      if (!open || !orderData) return;

      getSaleDealsMutate(orderData?.id);
      getAllConditionTypesMutate({});
    }, [orderData, open]);

    useEffect(() => {
      const [firstItem] = conditionTypes.options;

      if (!selectedRecord) {
        form.setFieldsValue({
          conditionType: firstItem?.value,
          type: firstItem?.title,
        });

        setCurrentProducts(
          productsApplied.filter(
            (product) => product?.ConditionType === firstItem?.value,
          ),
        );

        return;
      }

      setCurrentProducts([]);
    }, [open, conditionTypes, productsApplied, selectedRecord]);

    useEffect(() => {
      if (!selectedRecord) return;

      const isCurrency = selectedRecord?.Type?.toLowerCase() === 'vnd';
      const conditionValue = isCurrency
        ? formatCurrency({
            value: selectedRecord?.ConditionValue,
            round: true,
          })
        : selectedRecord?.ConditionValue;

      form.setFieldsValue({
        conditionType: selectedRecord?.ConditionType,
        type: selectedRecord?.Type,
        conditionValue: conditionValue,
        saleDeal: `${selectedRecord?.SaleDeal}_${selectedRecord?.Serial}`,
        note: selectedRecord?.Note,
      });

      setConditionValue(selectedRecord?.ConditionValue);

      setProducts(
        selectedRecord?.ConditionType.includes('H')
          ? []
          : selectedRecord?.records.map((record) => {
              const conditionValue = record?.ConditionValue || '0';
              const processPrice = Number(record?.ProcessPrice);
              const type = record?.Type;

              return {
                ...record,
                ConditionValue: isCurrency
                  ? formatCurrency({
                      value: Number(record?.ConditionValue ?? '0'),
                      round: true,
                    })
                  : record?.ConditionValue,
                ProcessPriceSap:
                  type === 'vnd'
                    ? `-${formatConditionValue(conditionValue)}`
                    : processPrice -
                      processPrice * (Number(conditionValue) / 100),
              } as any;
            }),
      );

      // getAllConditionTypesMutate({
      //   conditionTypeId: selectedRecord?.ConditionType,
      // });
    }, [selectedRecord]);

    useEffect(() => {
      if (!selectedRecord || products.length <= 0) return;

      if (!productStored.current) productStored.current = products;
    }, [products, productStored, selectedRecord]);

    useEffect(() => {
      if (products.length > 0) {
        const tableFormValues = tableForm.getFieldsValue();
        const type = form.getFieldValue('type');

        tableForm.setFieldsValue(
          Object.keys(tableFormValues).reduce((acc, key, index) => {
            if (!products[index]?.ConditionValue) {
              acc[key] = undefined;
            } else {
              const valueWithoutDots = products[
                index
              ]?.ConditionValue?.toString()
                .replace(/\./g, '')
                .replace(',', '.');

              acc[key] =
                type === '%'
                  ? Number(valueWithoutDots)
                  : formatCurrency({
                      value: Number(valueWithoutDots),
                      round: true,
                    });
            }

            return acc;
          }, {} as any),
        );
      }
    }, [products]);

    const handleConditionTypeScrollChange = useCallback(
      (params: ILazySelectParams) => {
        const { pageSize, value } = params;

        getAllConditionTypesMutate({
          conditionTypeId: value,
          pageSize,
        });
      },
      [],
    );

    const validate = (_: any, value: string) => {
      if (!value) return Promise.reject('Nhập giá trị');

      const type = form.getFieldValue('type');

      if (Number(value) < 0) return Promise.reject('Giá trị không được âm');
      if (type === '%' && Number(value) > 100) {
        return Promise.reject('Giá trị không vượt quá 100');
      }

      return Promise.resolve();
    };

    const formatConditionValue = (
      conditionValue: string | number | undefined,
    ) => {
      const valueWithoutDots =
        typeof conditionValue === 'string'
          ? conditionValue?.replace(/\./g, '').replace(',', '.')
          : conditionValue;

      return (
        form.getFieldValue('type')?.toLowerCase() === 'vnd'
          ? formatCurrency({
              value: Number(valueWithoutDots) || 0,
              round: true,
            })
          : valueWithoutDots || 0
      ).toString();
    };

    const columns = useMemo(() => {
      return [
        {
          title: 'STT',
          width: 50,
          render(_, __, index) {
            return index + 1;
          },
        },
        {
          title: 'Mã sản phẩm',
          width: 150,
          dataIndex: 'Material',
        },
        {
          title: 'Mã batch',
          width: 100,
          dataIndex: 'Batch',
        },
        {
          title: 'Đ/Giá công',
          dataIndex: 'ProcessPrice',
          align: 'right',
          currency: 'vi-VN',
          width: 100,
          render: (value) => {
            return formatCurrency({
              value,
              round: true,
            });
          },
        },
        {
          title: 'Đ/G công sau CK',
          dataIndex: 'ProcessPriceSap',
          align: 'right',
          currency: 'vi-VN',
          width: 100,
        },
        {
          title: 'Giá trị/phần trăm CK',
          dataIndex: 'ConditionValue',
          align: 'right',
          currency: 'vi-VN',
          width: 150,
          render: (value, record) => {
            return (
              <Form.Item
                className="m-0"
                name={`ConditionValue_${record?.Item}`}
                initialValue={value}
                rules={[
                  {
                    pattern: /^(?:\d{1,3}(?:\.\d{3})*|\d+)(\.\d+)?$/,
                    message: '',
                  },
                  {
                    validator: validate,
                  },
                ]}
              >
                <InputForm
                  value={value}
                  placeholder="Nhập giá trị"
                  onChange={(e) => {
                    const type = form.getFieldValue('type');
                    const value = e.target.value || '0';
                    const valueWithoutDots = value
                      .replace(/\./g, '')
                      .replace(',', '.');
                    const valueWithDots = formatNumber(
                      Number(valueWithoutDots),
                    );

                    setProducts((prev) =>
                      prev.map((item) => {
                        if (item?.Item === record?.Item) {
                          const processPrice = Number(item?.ProcessPrice);

                          return {
                            ...item,
                            ConditionValue:
                              type === 'vnd'
                                ? formatConditionValue(value)
                                : value,
                            ProcessPriceSap:
                              type === 'vnd'
                                ? `-${formatConditionValue(value)}`
                                : processPrice -
                                  processPrice * (Number(value) / 100),
                          };
                        }

                        return item;
                      }),
                    );

                    if (type === '%') return;

                    if (
                      !/^(?:\d{1,3}(?:\.\d{3})*|\d+)(\.\d+)?$/.test(
                        valueWithDots,
                      )
                    ) {
                      toast.dismiss();
                      toastError('Giá trị chỉ được nhập số!');
                      return;
                    }

                    tableForm.setFieldValue(
                      `ConditionValue_${record?.Item}`,
                      formatCurrency({
                        value: Number(valueWithoutDots),
                        round: true,
                      }),
                    );
                  }}
                />
              </Form.Item>
            );
          },
        },
        {
          title: 'Đơn vị',
          width: 100,
          render: (_, record) => {
            return record?.Type === '%' ? '%' : (record?.Currency ?? 'VND');
          },
        },
        {
          title: 'Thao tác',
          fixed: 'right',
          width: 100,
          align: 'center',
          key: 'action',
          render: (_, record) => {
            return (
              <Space size="middle">
                <ButtonAction
                  tooltip="Xóa"
                  onClick={() => {
                    setProducts((prev) =>
                      prev.filter((item) => item?.Item !== record?.Item),
                    );
                  }}
                >
                  <XIcon />
                </ButtonAction>
              </Space>
            );
          },
        },
      ] as ColumnsTypeCustom;
    }, [products]);

    const handleAddProduct = useCallback(() => {
      if (data.length <= 0) return;

      const formValues = form.getFieldsValue();

      if (!formValues.batch) {
        toastError('Vui lòng nhập mã Batch!');
        return;
      }

      const [product] = data
        .filter(
          (item) =>
            ![...products, ...currentProducts].some(
              (product) => product?.Item === item?.Item,
            ),
        )
        .reduce((acc, item) => {
          if (item?.Batch === formValues?.batch.trim()) {
            const conditionValue = formValues?.conditionValue || '0';
            const processPrice = Number(item?.ProcessPrice);

            acc.push({
              ...item,
              ConditionValue: conditionValue,
              Type: formValues?.type,
              ProcessPriceSap:
                formValues?.type === 'vnd'
                  ? `-${formatConditionValue(conditionValue)}`
                  : processPrice -
                    processPrice * (Number(conditionValue) / 100),
            });
          }

          return acc;
        }, [] as any[]);

      if (!product) {
        const index = data.findIndex(
          (product) => product?.Batch === formValues?.batch,
        );

        if (index >= 0) {
          toastError('Đã thêm sản phẩm này vào danh sách ưu đãi!');
          return;
        }

        toastError('Không tìm thấy sản phẩm!');
        return;
      }

      form.resetFields(['batch']);
      setProducts((prev) => [...prev, product]);
    }, [data, products, currentProducts, selectedRecord]);

    const handleGenerateVoucher = (
      formValues: any,
      tableFormValues: any,
      conditionTypes: ILazySelectData,
    ) => {
      const conditionType = formValues?.conditionType;
      const [saleDeal, serial] = formValues?.saleDeal?.split('_');
      const conditionTypeSelected = conditionTypes.options.find(
        (type) => type?.value === formValues?.conditionType,
      );

      const saleDealName =
        conditionTypeSelected?.label?.toString().split('-')[1].trim() ??
        formValues?.conditionType;

      if (!conditionType.includes('H'))
        return products.map((product) => ({
          SaleDeal: saleDeal,
          ConditionType: conditionType,
          ConditionValue:
            tableFormValues[`ConditionValue_${product?.Item}`].toString(),
          DiscountValue: '0.0',
          Note: formValues?.note ?? '',
          SaleDealName: saleDealName,
          Batch: product?.Batch,
          Item: product?.Item,
          Key: product?.Key,
          Material: product?.Material,
          ProcessPrice: product?.ProcessPrice,
          ProcessPriceSAP: product?.ProcessPriceSAP,
          TextScan: product?.TextScan,
          Type: product?.Type,
          Serial: serial,
        }));

      const conditionValue = formValues['conditionValue'].toString();

      return [
        {
          Key: orderData?.id,
          SaleDeal: saleDeal,
          ConditionType: conditionType,
          ConditionValue: conditionValue,
          DiscountValue: conditionValue,
          SaleDealName: saleDealName,
          Note: formValues['note'],
          Type: conditionTypeSelected?.title ?? formValues?.type,
          ...MANUAL_VOUCHER,
          Serial: serial,
        },
      ] as any;
    };

    const handleOnOk = useCallback(async () => {
      try {
        if (
          !(await form.validateFields()) ||
          !(await tableForm.validateFields())
        )
          return;

        const formValues = form.getFieldsValue();
        const tableFormValues = tableForm.getFieldsValue();

        const formFormatedValues = {
          ...formValues,
          conditionValue:
            formValues['type'].toLowerCase() === 'vnd'
              ? formValues['conditionValue']
                ? formValues['conditionValue']
                    .replace(/\./g, '')
                    .replace(',', '.')
                : undefined
              : formValues['conditionValue'],
        };
        const tableFormFormatedValues = Object.keys(tableFormValues).reduce(
          (acc, key) => {
            if (formValues['type'].toLowerCase() === 'vnd')
              acc[key] = tableFormValues[key]
                .replace(/\./g, '')
                .replace(',', '.');
            else acc[key] = tableFormValues[key];

            return acc;
          },
          {} as any,
        );

        const records = handleGenerateVoucher(
          formFormatedValues,
          tableFormFormatedValues,
          conditionTypes,
        );

        if (onBeforeOk && onBeforeOk(records[0]).isExist) return;

        onOk && onOk(records);
        handleOnCancel();
      } catch (error) {
        console.log(error);
      }
    }, [products, conditionTypes, onOk, onBeforeOk]);

    const handleOnCancel = useCallback(() => {
      setProducts([]);
      setConditionValue(undefined);

      form.resetFields();
      tableForm.resetFields();

      onCancel && onCancel();
    }, [onCancel]);

    const handleOnConditionTypeSelect = useCallback(
      async (_: any, record: DefaultOptionType) => {
        try {
          const handleResetTableForm = () => {
            tableForm.setFieldsValue(
              Object.keys(tableForm.getFieldsValue()).reduce((acc, key) => {
                acc[key] = undefined;
                return acc;
              }, {} as any),
            );
          };

          if (typeof conditionValue !== 'undefined') {
            const valueWithoutDots = conditionValue
              ?.toString()
              .replace(/\./g, '')
              .replace(',', '.');

            form.setFieldValue(
              'conditionValue',
              record?.title === '%'
                ? Number(valueWithoutDots)
                : formatCurrency({
                    value: Number(valueWithoutDots),
                    round: true,
                  }),
            );

            form.validateFields(['conditionValue']);
          }

          form.setFieldsValue({
            conditionType: record?.value,
            type: record?.title,
          });

          if (record?.value?.toString().includes('H')) {
            setProducts([]);
            form.setFieldValue('batch', undefined);
            return;
          }

          if (
            selectedRecord &&
            productStored.current &&
            record?.value === productStored.current[0]?.ConditionType
          ) {
            handleResetTableForm();
            setProducts(productStored.current);
          } else {
            handleResetTableForm();
            setProducts([]);
          }

          setCurrentProducts(
            productsApplied.filter(
              (product) => product?.ConditionType === record?.value,
            ),
          );
        } catch (error) {
          console.log(error);
        }
      },
      [conditionValue, productsApplied, selectedRecord, productStored],
    );

    const handleConditionValueChange = useCallback(
      (value: string) => {
        const formValues = form.getFieldsValue();
        const valueWithoutDots = value.replace(/\./g, '').replace(',', '.');
        const valueWithDots = formatNumber(Number(valueWithoutDots));
        const conditionValueFormatted = formatConditionValue(value);

        setConditionValue(Number(valueWithoutDots));

        setProducts((prev) =>
          prev.map((item) => {
            const processPrice = Number(products[0]?.ProcessPrice);

            return {
              ...item,
              ConditionValue: conditionValueFormatted,
              ProcessPriceSap:
                formValues?.type === 'vnd'
                  ? `-${conditionValueFormatted}`
                  : processPrice -
                    processPrice * (Number(formValues?.conditionValue) / 100),
            };
          }),
        );

        tableForm.setFieldsValue(
          Object.keys(tableForm.getFieldsValue()).reduce((acc, key) => {
            acc[key] = conditionValueFormatted;

            return acc;
          }, {} as any),
        );

        if (formValues['type'] === '%') return;

        if (!/^(?:\d{1,3}(?:\.\d{3})*|\d+)(\.\d+)?$/.test(valueWithDots)) {
          toast.dismiss();
          toastError('Giá trị chỉ được nhập số!');
          return;
        }

        form.setFieldValue(
          'conditionValue',
          formatCurrency({ value: Number(valueWithoutDots), round: true }),
        );
      },
      [products],
    );

    return (
      <Modal
        title="Nhập tay CTKM"
        open={open}
        className="min-w-[1000px]"
        onCancel={handleOnCancel}
        footer={
          <div className="flex justify-end mt-4 gap-2">
            <Button
              handleOnclick={handleOnCancel}
              customClass="!border-transparent !text-neutral-30 !bg-transparent"
              title="Quay lại"
            />
            <Button
              disabled={
                form.getFieldValue('conditionType')?.includes('H')
                  ? false
                  : products.length <= 0
              }
              fill
              title="Xác nhận"
              handleOnclick={handleOnOk}
            />
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={{ xs: 8, sm: 14 }}>
            <Col span={colSpan}>
              <Form.Item
                name="conditionType"
                label="Condition type"
                rules={[{ required: true, message: 'Chọn Condition type' }]}
              >
                <SelectForm
                  className="mb-2"
                  placeholder="Chọn Chương trình"
                  loading={isGetAllConditionTypesPending}
                  options={conditionTypes.options}
                  onSelect={handleOnConditionTypeSelect}
                />
                {/* <LazySelectForm
                  data={conditionTypes}
                  placeholder="Chọn Condition type"
                  loading={isGetAllConditionTypesPending}
                  onSelect={handleOnConditionTypeSelect}
                  onSelectScrollChange={handleConditionTypeScrollChange}
                /> */}
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="type"
                label="Loại KM"
                rules={[{ required: true }]}
              >
                <InputForm className="mb-2" readOnly placeholder="Loại KM" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="conditionValue"
                label="Giá trị/Phần trăm"
                rules={[
                  {
                    pattern: /^(?:\d{1,3}(?:\.\d{3})*|\d+)(\.\d+)?$/,
                    message: '',
                  },
                  {
                    validator: (_, value) => {
                      if (
                        !form.getFieldValue('conditionType')?.includes('H') &&
                        !value
                      )
                        return Promise.resolve();

                      return validate(_, value);
                    },
                  },
                ]}
              >
                <InputForm
                  className="mb-2"
                  placeholder="Giá trị/Phần trăm"
                  onChange={(e) =>
                    handleConditionValueChange(e.target.value ?? '0')
                  }
                />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item
                name="saleDeal"
                label="Chọn Chương trình"
                rules={[
                  { required: true, message: 'Chọn chương trình ưu đãi' },
                ]}
              >
                <SelectForm
                  className="mb-2"
                  loading={isGetSaleDealsPending}
                  placeholder="Chọn Chương trình"
                  options={saleDeals}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item name="note" label="Ghi chú" labelCol={{ span: 24 }}>
                <Input.TextArea
                  showCount
                  className="mb-4"
                  maxLength={250}
                  placeholder="Nhập ghi chú (giới hạn 250 ký tự)"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[8, 8]}>
            <Col span={5}>
              <Form.Item name="batch">
                <InputForm
                  placeholder="Nhập mã batch"
                  className="mb-2"
                  disabled={form.getFieldValue('conditionType')?.includes('H')}
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Button
                fill
                title="Thêm"
                customClass="!mt-0"
                type="button"
                disabled={form.getFieldValue('conditionType')?.includes('H')}
                handleOnclick={handleAddProduct}
              />
            </Col>
          </Row>
        </Form>
        <Form form={tableForm}>
          <TableComponent
            className="mb-4"
            dataSource={products}
            columns={columns}
            pagination={false}
            scroll={{ x: 1200 }}
            rowKey={(record) => record?.Item ?? ''}
          />
        </Form>
      </Modal>
    );
  },
);
